<template>
  <main class="biographies-page">
    <nav class="biographies-compare" aria-label="Compare Biographies designs">
      <span aria-current="page">Version 1</span>
      <router-link to="/biographies-v2">View version 2 ↗</router-link>
    </nav>
    <header class="directory-intro">
      <span class="directory-eyebrow">A world of stories</span>
      <h1>Biographies</h1>
      <p>Discover the people, memories, and life's work behind every story.</p>
    </header>

    <v-alert
      v-if="error"
      type="error"
      outlined
      role="alert"
    >
      {{ error }}
      <v-btn
        text
        small
        @click="loadProfiles"
      >
        Try again
      </v-btn>
    </v-alert>

    <section
      v-if="featured"
      class="featured-section"
      aria-labelledby="featured-heading"
    >
      <h2 id="featured-heading">
        Featured Biographies
      </h2>
      <router-link
        :to="profileLink(featured)"
        class="featured-biography"
      >
        <div class="featured-portrait">
          <v-img
            :src="avatar(featured)"
            eager
            :alt="featured.name"
            position="center"
            height="100%"
            @error="markFailed(featured)"
          />
        </div>
        <div class="featured-copy">
          <span class="featured-kicker"><v-icon
            small
            color="white"
          >{{ icons.mdiStar }}</v-icon> Featured biography</span>
          <h3>Pepe Moreno</h3>
          <p class="featured-username">
            @{{ featured.username }}
          </p>
          <p class="featured-description">
            {{ excerpt(featured.bio) || 'Explore the memories, milestones, and life behind the story.' }}
          </p>
          <span class="featured-cta">Explore biography <v-icon color="white">{{ icons.mdiArrowRight }}</v-icon></span>
        </div>
      </router-link>
    </section>
    <v-skeleton-loader
      v-else-if="loading"
      type="image"
      class="mb-8"
    />

    <section
      class="explore-section"
      aria-labelledby="explore-heading"
      :aria-busy="String(loading)"
    >
      <div class="explore-heading">
        <div>
          <h2 id="explore-heading">
            Explore Biographies
          </h2>
          <p>Find a familiar name or discover someone new.</p>
        </div>
        <form
          class="directory-filters"
          role="search"
          @submit.prevent="searchProfiles"
        >
          <v-text-field
            v-model="search"
            label="Search by name or username"
            :prepend-inner-icon="icons.mdiMagnify"
            outlined
            dense
            hide-details
            clearable
            @click:clear="clearSearch"
          />
          <v-btn
            type="submit"
            color="primary"
            :disabled="loading"
          >
            Search
          </v-btn>
          <v-select
            v-model="sort"
            :items="sortOptions"
            label="Sort by"
            outlined
            dense
            hide-details
            @change="searchProfiles"
          />
        </form>
      </div>

      <div
        v-if="loading"
        class="biography-grid"
        aria-label="Loading biographies"
      >
        <v-skeleton-loader
          v-for="n in 6"
          :key="n"
          type="card"
        />
      </div>
      <div
        v-else-if="users.length"
        class="biography-grid"
      >
        <router-link
          v-for="user in users"
          :key="user.id"
          :to="profileLink(user)"
          class="biography-card"
        >
          <div class="biography-cover">
            <img
              :src="cover(user)"
              :alt="`Biography of ${user.name || user.username}`"
              :style="coverStyle(user)"
              loading="lazy"
              @error="advanceCover(user)"
            />
            <span class="biography-cover-action"><v-icon color="white">{{ icons.mdiArrowTopRight }}</v-icon></span>
          </div>
          <div class="biography-card-copy">
            <h3>{{ user.name || user.username }}</h3>
            <span class="biography-username">@{{ user.username }}</span>
            <p>{{ excerpt(user.bio) || 'Discover their public biography and the memories they have chosen to share.' }}</p>
            <span class="biography-card-link">Explore biography</span>
          </div>
        </router-link>
      </div>
      <div
        v-else-if="!error"
        class="directory-empty"
        role="status"
      >
        <v-icon
          size="40"
          color="primary"
        >
          {{ icons.mdiBookOpenVariant }}
        </v-icon>
        <h3>{{ appliedSearch ? 'No biographies found' : 'More stories are on their way' }}</h3>
        <p>{{ appliedSearch ? 'Try another name or username.' : 'Public biographies will appear here as people share their stories.' }}</p>
        <v-btn
          v-if="appliedSearch"
          text
          color="primary"
          @click="clearSearch"
        >
          Clear search
        </v-btn>
      </div>
      <nav
        v-if="!loading && (page > 1 || hasMore)"
        class="directory-pagination"
        aria-label="Biography pages"
      >
        <v-btn
          outlined
          :disabled="page === 1"
          @click="changePage(-1)"
        >
          Previous
        </v-btn>
        <span aria-live="polite">Page {{ page }}</span>
        <v-btn
          outlined
          :disabled="!hasMore"
          @click="changePage(1)"
        >
          Next
        </v-btn>
      </nav>
    </section>
  </main>
</template>

<script>
import axios from 'axios'
import {
  mdiStar, mdiArrowRight, mdiArrowTopRight, mdiMagnify, mdiBookOpenVariant,
} from '@mdi/js'

export default {
  name: 'Biographies',
  data: () => ({
    icons: {
      mdiStar, mdiArrowRight, mdiArrowTopRight, mdiMagnify, mdiBookOpenVariant,
    },
    users: [],
    featured: null,
    search: '',
    appliedSearch: '',
    sort: 'newest',
    sortOptions: [{ text: 'Newest profiles', value: 'newest' }, { text: 'Name A to Z', value: 'name' }],
    page: 1,
    hasMore: false,
    loading: false,
    error: '',
    requestId: 0,
    failedAvatars: {},
    defaultAvatar: require('@/assets/images/biographies/default-cover.webp'),
    coverAttempts: {},
  }),
  mounted() {
    this.loadProfiles()
  },
  beforeDestroy() {
    this.requestId += 1
  },
  methods: {
    coverOptions(user) {
      return [
        user.cover_url && { url: user.cover_url, rotation: user.cover_rotation || 0 },
        user.avatar && { url: `https://self-biography-system.s3.us-east-2.amazonaws.com/${user.avatar}`, rotation: 0 },
        { url: this.defaultAvatar, rotation: 0 },
      ].filter(Boolean)
    },
    cover(user) {
      return this.coverOptions(user)[this.coverAttempts[user.id] || 0].url
    },
    coverStyle(user) {
      const { rotation } = this.coverOptions(user)[this.coverAttempts[user.id] || 0]
      const angle = ((Number(rotation) % 4) + 4) % 4

      return {
        transform: `translate(-50%, -50%) rotate(${angle * 90}deg)`,
        width: angle % 2 ? '66.6667%' : '100%',
        height: angle % 2 ? '150%' : '100%',
      }
    },
    advanceCover(user) {
      const attempt = this.coverAttempts[user.id] || 0
      if (attempt < this.coverOptions(user).length - 1) this.$set(this.coverAttempts, user.id, attempt + 1)
    },
    profileLink(user) {
      return { name: 'public_biography', params: { username: user.username }, query: { ct: '-1' } }
    },
    avatar(user) {
      if (!user.avatar || this.failedAvatars[user.id]) return this.defaultAvatar

      return `https://self-biography-system.s3.us-east-2.amazonaws.com/${user.avatar}`
    },
    markFailed(user) {
      this.$set(this.failedAvatars, user.id, true)
    },
    excerpt(value) {
      const element = document.createElement('div')
      element.innerHTML = value || ''

      return (element.textContent || '').replace(/\s+/g, ' ').trim()
    },
    searchProfiles() {
      this.appliedSearch = (this.search || '').trim()
      this.page = 1
      this.loadProfiles()
    },
    clearSearch() {
      this.search = ''
      this.searchProfiles()
    },
    async changePage(direction) {
      this.page += direction
      await this.loadProfiles()
      document.getElementById('explore-heading').scrollIntoView({ block: 'start' })
    },
    async loadProfiles() {
      this.requestId += 1
      const { requestId } = this
      this.loading = true
      this.error = ''
      try {
        const { data } = await axios.get(`${process.env.VUE_APP_ROOT_API}/getPublicUser`, {
          params: {
            directory: '1', page: this.page, q: this.appliedSearch, sort: this.sort,
          },
        })
        if (requestId !== this.requestId) return
        this.coverAttempts = {}
        this.users = data.users || []
        this.featured = data.featured || null
        this.hasMore = !!data.hasMore
      } catch (_) {
        if (requestId !== this.requestId) return
        this.users = []
        this.featured = null
        this.hasMore = false
        this.error = 'We could not load biographies. Please try again.'
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.biographies-compare { display: flex; justify-content: flex-end; align-items: center; gap: 18px; margin-bottom: 20px; font-size: 12px; }
.biographies-compare a { text-decoration: none; font-weight: 600; }

.biographies-page { max-width: 1180px; margin: 0 auto; padding: 24px 0 48px; }
.directory-intro { max-width: 680px; margin-bottom: 36px; }
.directory-eyebrow { color: #9155fd; font-size: 12px; font-weight: 700; letter-spacing: .14em; text-transform: uppercase; }
.directory-intro h1 { font-size: clamp(30px, 4vw, 44px); line-height: 1.2; margin: 12px 0; }
.directory-intro p, .explore-heading p { font-size: 16px; line-height: 1.6; margin-bottom: 0; }
h2 { font-size: 22px; margin-bottom: 18px; }
.featured-section { margin-bottom: 48px; }
.featured-biography { display: grid; grid-template-columns: minmax(240px, 1.1fr) 1fr; overflow: hidden; border-radius: 10px; background: linear-gradient(120deg, #312d4b, #463466); color: #fff !important; text-decoration: none; box-shadow: 0 8px 24px rgba(49,45,75,.14); }
.featured-portrait { position: relative; min-height: 340px; background: #312d4b; }
.featured-portrait > .v-image { position: absolute; inset: 0; width: 100%; }
.featured-copy { padding: 40px; align-self: center; }
.featured-kicker { display: inline-flex; align-items: center; gap: 8px; font-size: 12px; letter-spacing: .08em; text-transform: uppercase; color: #e5d7ff; }
.featured-copy h3 { font-size: clamp(28px, 4vw, 38px); line-height: 1.2; margin: 18px 0 6px; color: #fff; }
.featured-username { color: #d4c5ee; font-size: 14px; }
.featured-description { color: #eee8f6; font-size: 16px; line-height: 1.7; display: -webkit-box; -webkit-line-clamp: 4; -webkit-box-orient: vertical; overflow: hidden; }
.featured-cta { display: inline-flex; align-items: center; gap: 16px; background: #9155fd; border-radius: 5px; padding: 10px 16px; margin-top: 12px; font-size: 14px; font-weight: 600; }
.explore-heading { margin-bottom: 24px; }
.explore-heading h2 { margin-bottom: 6px; scroll-margin-top: 85px; }
.directory-filters { display: grid; grid-template-columns: minmax(200px, 1fr) auto 200px; gap: 12px; align-items: center; margin-top: 24px; }
.biography-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 24px; }
.biography-card { display: flex; flex-direction: column; overflow: hidden; border: 1px solid rgba(94,86,105,.15); border-radius: 8px; background: #fff; color: inherit !important; text-decoration: none; box-shadow: 0 2px 8px rgba(49,45,75,.06); transition: border-color .2s, box-shadow .2s; }
.biography-card:hover { border-color: #9155fd; box-shadow: 0 5px 16px rgba(145,85,253,.14); }
.biography-cover { position: relative; aspect-ratio: 3 / 2; overflow: hidden; background: #312d4b; }
.biography-cover > img { position: absolute; top: 50%; left: 50%; object-fit: cover; }
.biography-cover-action { position: absolute; right: 16px; bottom: 16px; display: flex; padding: 8px; border-radius: 50%; background: rgba(49,45,75,.7); }
.biography-card-copy { flex: 1; display: flex; flex-direction: column; padding: 22px; }
.biography-card h3 { font-size: 18px; line-height: 1.4; overflow-wrap: anywhere; margin-bottom: 4px; }
.biography-username { font-size: 13px; opacity: .8; overflow-wrap: anywhere; }
.biography-card p { font-size: 14px; line-height: 1.65; margin: 16px 0 24px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.biography-card-link { color: #7940dd; font-size: 14px; font-weight: 600; margin-top: auto; }
.biography-card:focus-visible, .featured-biography:focus-visible { outline: 3px solid #9155fd; outline-offset: 4px; }
.directory-empty { padding: 48px 20px; text-align: center; border: 1px dashed rgba(145,85,253,.4); border-radius: 8px; }
.directory-empty h3 { font-size: 18px; margin: 16px 0 8px; }
.directory-pagination { display: flex; justify-content: center; align-items: center; gap: 20px; margin-top: 32px; }
.theme--dark .biography-card { background: #312d4b; border-color: #4a445f; }
.theme--dark .biography-card-link, .theme--dark .directory-eyebrow { color: #c8afff; }
@media (max-width: 900px) { .biography-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } .featured-copy { padding: 28px; } }
@media (max-width: 600px) {
  .biographies-page { padding-top: 12px; }
  .featured-biography { grid-template-columns: 1fr; }
  .featured-portrait { height: 280px; min-height: 0; }
  .featured-copy { padding: 24px; }
  .directory-filters { grid-template-columns: minmax(0, 1fr) auto; }
  .directory-filters .v-select { grid-column: 1 / -1; }
  .biography-grid { grid-template-columns: 1fr; gap: 16px; }
  .directory-pagination { gap: 12px; }
}
</style>
